import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PhotoUploader } from "@/components/common/PhotoUploader";
import { assetsService } from "@/services/assets.service";
import { customersService } from "@/services/customers.service";
import { getErrorMessage } from "@/services/api";
import {
    ASSET_CATEGORIES,
    OPERATIONAL_STATUS_OPTIONS,
    PHOTO_CATEGORIES,
} from "@/config/constants";

const schema = z.object({
    category: z.string().min(1, "Escolha uma categoria"),
    customer_id: z.string().min(1, "Selecione o cliente"),
    manufacturer: z.string().optional().nullable(),
    model: z.string().optional().nullable(),
    serial_number: z.string().optional().nullable(),
    customer_asset_tag: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    voltage: z.string().optional().nullable(),
    power: z.string().optional().nullable(),
    current: z.string().optional().nullable(),
    rpm: z.string().optional().nullable(),
    frequency: z.string().optional().nullable(),
    weight: z.string().optional().nullable(),
    manufacturing_year: z.coerce.number().int().min(1900).max(2100).optional().nullable().or(z.literal(null)),
    unit: z.string().optional().nullable(),
    sector: z.string().optional().nullable(),
    installation_location: z.string().optional().nullable(),
    notes: z.string().optional().nullable(),
    operational_status: z.string(),
    photos: z.any().optional(),
});

function Field({ label, error, children, className = "", required = false }) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            <Label className="text-sm font-medium">
                {label} {required && <span className="text-danger">*</span>}
            </Label>
            {children}
            {error && <div className="text-xs text-danger">{error}</div>}
        </div>
    );
}

export default function AssetFormPage() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [photoTab, setPhotoTab] = useState(PHOTO_CATEGORIES[0]);

    const preselectedCustomer = searchParams.get("customer_id") || "";

    const detail = useQuery({
        queryKey: ["asset", id],
        queryFn: () => assetsService.get(id),
        enabled: isEdit,
    });

    const customers = useQuery({
        queryKey: ["customers", "select"],
        queryFn: () => customersService.list({ pageSize: 100 }),
    });

    const {
        register, handleSubmit, reset, watch, setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            category: "Motor Elétrico",
            customer_id: preselectedCustomer || "",
            manufacturer: "",
            model: "",
            serial_number: "",
            customer_asset_tag: "",
            description: "",
            voltage: "",
            power: "",
            current: "",
            rpm: "",
            frequency: "",
            weight: "",
            manufacturing_year: "",
            unit: "",
            sector: "",
            installation_location: "",
            notes: "",
            operational_status: "operante",
            photos: [],
        },
    });

    React.useEffect(() => {
        if (isEdit && detail.data) {
            reset({
                ...detail.data,
                customer_id: detail.data.customer_id || "",
                manufacturing_year: detail.data.manufacturing_year ?? "",
                notes: detail.data.notes || "",
                photos: detail.data.photos || [],
                operational_status: detail.data.operational_status || "operante",
            });
        }
    }, [isEdit, detail.data, reset]);

    const save = useMutation({
        mutationFn: (data) => {
            const payload = { ...data };
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "") payload[k] = null;
            });
            if (payload.manufacturing_year === null || payload.manufacturing_year === "") {
                delete payload.manufacturing_year;
            }
            return isEdit ? assetsService.update(id, payload) : assetsService.create(payload);
        },
        onSuccess: (data) => {
            toast.success(isEdit ? "Equipamento atualizado." : "Equipamento cadastrado.");
            qc.invalidateQueries({ queryKey: ["assets"] });
            qc.invalidateQueries({ queryKey: ["asset", data?.id] });
            // If opened from WO form via new tab (with customer_id query param),
            // signal parent tab to refresh the equipment list.
            if (preselectedCustomer && typeof window !== "undefined") {
                try {
                    window.localStorage.setItem(
                        `prosper360:new-equipment:${preselectedCustomer}`,
                        JSON.stringify({ id: data?.id, at: Date.now() }),
                    );
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn("[AssetForm] failed to signal parent tab:", err);
                }
            }
            navigate(`/assets/${data?.id || id}`);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao salvar equipamento.")),
    });

    const photos = watch("photos") || [];
    const photosByCat = React.useMemo(() => {
        const map = {};
        PHOTO_CATEGORIES.forEach((c) => (map[c] = []));
        photos.forEach((p) => {
            const cat = (p && p.category) || "Geral";
            (map[cat] = map[cat] || []).push(p);
        });
        return map;
    }, [photos]);

    function updateCategoryPhotos(catLabel, next) {
        const others = photos.filter((p) => (p.category || "Geral") !== catLabel);
        const withCat = (next || []).map((p) =>
            typeof p === "string" ? { url: p, category: catLabel } : { ...p, category: catLabel },
        );
        setValue("photos", [...others, ...withCat], { shouldDirty: true });
    }

    return (
        <form onSubmit={handleSubmit(save.mutate)} className="space-y-6">
            <PageHeader
                title={isEdit ? "Editar equipamento" : "Novo equipamento"}
                description="Cadastro completo com dados técnicos, localização e fotos."
                backTo={isEdit ? `/assets/${id}` : "/assets"}
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Equipamentos", to: "/assets" },
                    { label: isEdit ? "Editar" : "Novo" },
                ]}
                actions={
                    <>
                        <Button type="button" variant="ghost" onClick={() => navigate("/assets")}>
                            Cancelar
                        </Button>
                        <Button type="submit"
                            className="bg-secondary text-secondary-foreground hover:opacity-90"
                            disabled={save.isPending}
                            data-testid="asset-save-button">
                            {save.isPending
                                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                : <Save className="mr-2 h-4 w-4" />}
                            Salvar
                        </Button>
                    </>
                }
            />

            <div className="space-y-6">
                {/* 1) Informações */}
                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Informações</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-3">
                        <Field label="Cliente" required error={errors.customer_id?.message}>
                            <Select value={watch("customer_id") || ""}
                                onValueChange={(v) => setValue("customer_id", v)}>
                                <SelectTrigger data-testid="asset-customer">
                                    <SelectValue placeholder="Selecionar cliente" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(customers.data?.items || []).map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Categoria" required error={errors.category?.message}>
                            <Select value={watch("category")} onValueChange={(v) => setValue("category", v)}>
                                <SelectTrigger data-testid="asset-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ASSET_CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Situação">
                            <Select value={watch("operational_status")}
                                onValueChange={(v) => setValue("operational_status", v)}>
                                <SelectTrigger data-testid="asset-op-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {OPERATIONAL_STATUS_OPTIONS.map((s) => (
                                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Fabricante">
                            <Input {...register("manufacturer")} data-testid="asset-manufacturer" />
                        </Field>
                        <Field label="Modelo">
                            <Input {...register("model")} data-testid="asset-model" />
                        </Field>
                        <Field label="Número de Série">
                            <Input {...register("serial_number")} data-testid="asset-serial" />
                        </Field>
                        <Field label="Patrimônio do cliente">
                            <Input {...register("customer_asset_tag")}
                                placeholder="ex: TAG-45912"
                                data-testid="asset-customer-tag" />
                        </Field>
                        <Field label="Ano de fabricação">
                            <Input type="number" {...register("manufacturing_year")} />
                        </Field>
                        <Field label="Descrição" className="md:col-span-3">
                            <Textarea rows={2} {...register("description")}
                                placeholder="Descrição adicional do equipamento…" />
                        </Field>
                    </CardContent>
                </Card>

                {/* 2) Dados Técnicos */}
                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Dados Técnicos</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-3">
                        <Field label="Potência">
                            <Input {...register("power")} placeholder="ex: 150 CV / 110 kW" />
                        </Field>
                        <Field label="Tensão">
                            <Input {...register("voltage")} placeholder="ex: 220/380V" data-testid="asset-voltage" />
                        </Field>
                        <Field label="Corrente">
                            <Input {...register("current")} placeholder="ex: 210 A" data-testid="asset-current" />
                        </Field>
                        <Field label="RPM">
                            <Input {...register("rpm")} placeholder="ex: 1750" />
                        </Field>
                        <Field label="Frequência">
                            <Input {...register("frequency")} placeholder="ex: 60 Hz" />
                        </Field>
                        <Field label="Peso">
                            <Input {...register("weight")} placeholder="ex: 45 kg" />
                        </Field>
                    </CardContent>
                </Card>

                {/* 3) Localização */}
                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Localização</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-3">
                        <Field label="Unidade">
                            <Input {...register("unit")} placeholder="ex: Planta São Paulo" data-testid="asset-unit" />
                        </Field>
                        <Field label="Setor">
                            <Input {...register("sector")} placeholder="ex: Produção A" data-testid="asset-sector" />
                        </Field>
                        <Field label="Local de instalação">
                            <Input {...register("installation_location")}
                                placeholder="ex: Bomba centrífuga #3, estação 12"
                                data-testid="asset-install-location" />
                        </Field>
                    </CardContent>
                </Card>

                {/* 4) Fotos */}
                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Fotos</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={photoTab} onValueChange={setPhotoTab} className="w-full">
                            <TabsList className="flex-wrap">
                                {PHOTO_CATEGORIES.map((c) => (
                                    <TabsTrigger key={c} value={c} data-testid={`photos-tab-${c}`}>
                                        {c}
                                        {photosByCat[c]?.length > 0 && (
                                            <span className="ml-1.5 rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">
                                                {photosByCat[c].length}
                                            </span>
                                        )}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                            {PHOTO_CATEGORIES.map((c) => (
                                <TabsContent key={c} value={c} className="pt-3">
                                    <PhotoUploader multiple photoWithMeta
                                        value={photosByCat[c]}
                                        onChange={(next) => updateCategoryPhotos(c, next)}
                                        testId={`asset-photos-${c}`} />
                                </TabsContent>
                            ))}
                        </Tabs>
                    </CardContent>
                </Card>

                {/* 5) Observações */}
                <Card className="rounded-xl border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Observações</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Textarea rows={3} {...register("notes")}
                            placeholder="Observações sobre este equipamento…" />
                    </CardContent>
                </Card>
            </div>
        </form>
    );
}
